import { useState } from "react";
import { Copy, Loader2, Landmark, Smartphone, Coins, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, usd, fmtDate } from "@/lib/api";
import { useFetch, PageHeader, Panel, Loading, StatusBadge, Table, Empty } from "@/components/common";
import { DepositSupport, ReviewNotice } from "@/components/support";

const ICONS = { bank: Landmark, upi: Smartphone, usdt_trc20: Coins, usdt_bep20: Coins };
const DETAIL_LABELS = { bank_name: "Bank", account_name: "Account name", account_number: "Account number", ifsc: "IFSC / SWIFT", upi_id: "UPI ID", address: "Wallet address" };

const copy = (v) => { navigator.clipboard?.writeText(String(v)); toast.success("Copied"); };

function PayStep({ dep, onDone, onCancel }) {
  const [txn, setTxn] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/deposits/${dep.id}/submit`, { txn_id: txn });
      toast.success("Your deposit request is under review. Please allow up to 4 hours.");
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  const details = Object.entries(dep.payment_details || {}).filter(([k]) => k !== "enabled");
  return (
    <Panel title={`Complete payment · ${dep.method_label}`} testid="deposit-pay-step"
      action={<button onClick={onCancel} className="text-xs text-slate-400 hover:text-red-400 flex items-center gap-1" data-testid="cancel-deposit-btn"><X size={13} />Cancel request</button>}>
      <div className="grid gap-6 p-6 lg:grid-cols-2">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400">Send exactly</p>
          <div className="mt-2 flex items-center gap-3">
            <p className="num text-4xl font-semibold text-[#10B981]" data-testid="unique-deposit-amount">{usd(dep.amount)}</p>
            <button onClick={() => copy(dep.amount)} className="nx-btn-ghost px-2 py-1.5" data-testid="copy-amount-btn"><Copy size={14} /></button>
          </div>
          <p className="mt-2 text-xs text-slate-500 leading-relaxed">This unique amount identifies your payment. Send the exact amount (USD or equivalent) to the details below, then submit your transaction ID / UTR / hash.</p>
          <div className="mt-5 space-y-2">
            {details.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3 rounded-lg border border-[#1E2738] bg-[#0B0E14] px-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-[11px] text-slate-500">{DETAIL_LABELS[k] || k}</p>
                  <p className="num truncate text-sm text-white" data-testid={`pay-detail-${k}`}>{v}</p>
                </div>
                <button onClick={() => copy(v)} className="text-slate-400 hover:text-[#10B981]" data-testid={`copy-${k}`}><Copy size={14} /></button>
              </div>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4 rounded-xl border border-[#1E2738] bg-[#0B0E14] p-5">
          <div>
            <label className="nx-label">Transaction ID / UTR / Hash</label>
            <input className="nx-input num" required minLength={4} value={txn} onChange={(e) => setTxn(e.target.value)} placeholder="Paste your payment reference" data-testid="transaction-id-input" />
          </div>
          <p className="text-xs text-slate-500">Your deposit stays <span className="text-amber-400">pending</span> until our team verifies the payment manually.</p>
          <button className="nx-btn-primary mt-auto py-3" disabled={busy} data-testid="submit-deposit-button">{busy && <Loader2 size={16} className="animate-spin" />}Submit for verification</button>
        </form>
      </div>
    </Panel>
  );
}

export default function Deposit() {
  const { data: opt, loading } = useFetch("/deposit/options");
  const { data: deps, reload } = useFetch("/deposits");
  const [method, setMethod] = useState(null);
  const [tier, setTier] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [helpFor, setHelpFor] = useState(null);
  if (loading || !opt) return <Loading />;
  const hasPending = deps?.some((d) => d.status === "pending");
  const methods = Object.entries(opt.methods);
  const m = method || methods[0]?.[0];

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/deposits", { tier, method: m });
      setActive(data);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (id) => {
    try {
      await api.post(`/deposits/${id}/cancel`);
      setActive(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Funding" title="Deposit funds" subtitle="Choose a method and tier — you'll get a unique amount to pay." />
      {hasPending && <ReviewNotice />}
      {active ? (
        <PayStep dep={active} onDone={() => { setActive(null); reload(); }} onCancel={() => cancel(active.id)} />
      ) : (
        <Panel testid="deposit-select-step">
          <div className="space-y-6 p-6">
            <div>
              <p className="nx-label">1 · Payment method</p>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {methods.length === 0 && <p className="text-sm text-slate-500">No payment methods are available right now.</p>}
                {methods.map(([k, v]) => {
                  const Icon = ICONS[k];
                  return (
                    <button key={k} onClick={() => setMethod(k)} data-testid={`deposit-method-${k}`}
                      className={`flex items-center gap-3 rounded-xl border p-4 text-left text-sm transition-colors ${m === k ? "border-[#10B981] bg-[#10B981]/10 text-white" : "border-[#242F42] bg-[#0B0E14] text-slate-300 hover:border-[#334155]"}`}>
                      <Icon size={18} className={m === k ? "text-[#10B981]" : "text-slate-500"} />{v.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="nx-label">2 · Amount tier (USD)</p>
              <div className="grid grid-cols-3 gap-3 md:grid-cols-6">
                {opt.tiers.map((t) => (
                  <button key={t} onClick={() => setTier(t)} data-testid={`deposit-tier-button-${t}`}
                    className={`num rounded-xl border py-4 text-base font-semibold transition-colors ${tier === t ? "border-[#10B981] bg-[#10B981]/10 text-[#10B981]" : "border-[#242F42] bg-[#0B0E14] text-white hover:border-[#334155]"}`}>
                    ${t}
                  </button>
                ))}
              </div>
            </div>
            <button className="nx-btn-primary px-6 py-3" disabled={!tier || !m || busy} onClick={create} data-testid="generate-deposit-btn">
              {busy && <Loader2 size={16} className="animate-spin" />} Continue to payment
            </button>
          </div>
        </Panel>
      )}

      <Panel title="Deposit history">
        <Table head={["Date", "Method", "Amount", "Transaction ID", "Status", ""]} testid="deposit-history-table">
          {!deps?.length && <Empty cols={6} text="No deposits yet" />}
          {deps?.map((d) => (
            <tr key={d.id} data-testid={`deposit-row-${d.id}`}>
              <td className="nx-td">{fmtDate(d.created_at)}</td>
              <td className="nx-td">{d.method_label}</td>
              <td className="nx-td num text-white">{usd(d.amount)}</td>
              <td className="nx-td num">{d.txn_id || "—"}</td>
              <td className="nx-td"><StatusBadge status={d.status} /></td>
              <td className="nx-td text-right">
                {d.status === "awaiting_payment" && (
                  <button className="text-xs text-[#10B981] hover:underline" onClick={() => setActive(d)} data-testid={`continue-deposit-${d.id}`}>Continue</button>
                )}
                {d.admin_note && <span className="text-xs text-slate-500">{d.admin_note}</span>}
                {["pending", "rejected"].includes(d.status) && (
                  <button className="ml-3 text-xs text-slate-400 hover:text-[#10B981]" onClick={() => setHelpFor({ ...d })} data-testid={`deposit-help-${d.id}`}>Get help</button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </Panel>

      <DepositSupport prefill={helpFor} onPrefillUsed={() => setHelpFor(null)} />
    </div>
  );
}
